import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Country, State } from 'country-state-city';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { useAuthFormToast } from '../components/auth/useAuthFormToast';
import {
  AUTH_PRIMARY,
  authFieldSx,
  authPrimaryButtonSx,
  authSectionLabelSx,
  phoneInputStyles,
} from '../components/auth/authFormStyles';

const sectionCardSx = {
  width: '100%',
  p: 2,
  mb: 2,
  borderRadius: 2,
  border: '1px solid #E8EAED',
  bgcolor: '#FAFAFA',
} as const;

const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 1.5,
} as const;

export const CompleteProfilePage: React.FC = () => {
  const {
    client,
    isAuthenticated,
    hasCompletedProfile,
    pendingProfileUser,
    completeProfile,
    clearPendingProfileUser,
  } = useAuth();
  const navigate = useNavigate();

  const countries = useMemo(() => Country.getAllCountries(), []);

  const [country, setCountry] = useState<{ isoCode: string; name: string } | null>(null);
  const [state, setState] = useState<{ isoCode: string; name: string } | null>(null);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const { showError, ToastHost } = useAuthFormToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const states = useMemo(
    () => (country ? State.getStatesOfCountry(country.isoCode) : []),
    [country]
  );

  useEffect(() => {
    if (!client) return;

    if (client.phone) {
      setPhone(client.phone);
    }

    const countryCode = client.address?.country?.trim();
    if (countryCode) {
      const matchedCountry =
        countries.find((c) => c.isoCode === countryCode) ??
        countries.find((c) => c.name === countryCode);
      if (matchedCountry) {
        setCountry(matchedCountry);
      }
    }

    const stateCode = client.address?.state_province?.trim();
    if (stateCode && countryCode) {
      const matchedState =
        State.getStatesOfCountry(countryCode).find((s) => s.isoCode === stateCode) ??
        State.getStatesOfCountry(countryCode).find((s) => s.name === stateCode);
      if (matchedState) {
        setState(matchedState);
      }
    }
  }, [client, countries]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (hasCompletedProfile) return <Navigate to="/" replace />;

  const isNewUser = pendingProfileUser?.isNewSocialUser;
  const subtitle = isNewUser
    ? 'Welcome to Canadawale Yar! Tell us a bit about yourself so we can personalize your experience.'
    : 'We need a few more details to complete your account.';

  const phoneDigits = phone.replace(/\D/g, '');
  const isFormValid = country && state && phoneDigits.length >= 7;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!country || !state || !phone) {
      showError('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      await completeProfile(phone, country.isoCode, state.isoCode);
      clearPendingProfileUser();

      if (pendingProfileUser?.isNewSocialUser) {
        setShowPasswordPrompt(true);
      } else {
        navigate('/', { replace: true });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPassword = () => {
    navigate('/', { replace: true });
  };

  const handleSavePassword = async () => {
    if (password !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      showError('Password must be at least 6 characters');
      return;
    }
    navigate('/', { replace: true });
  };

  return (
    <>
      <AuthPageShell title="Complete your profile" subtitle={subtitle} maxWidth={480}>
        {client?.name && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              bgcolor: '#FFF5F5',
              border: '1px solid #FFD6D6',
            }}
          >
            <PersonOutlineIcon sx={{ color: AUTH_PRIMARY, fontSize: 28 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F1111' }}>
                {client.name}
              </Typography>
              {client.email && (
                <Typography variant="caption" sx={{ color: '#565959' }}>
                  {client.email}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Box sx={sectionCardSx}>
            <Box sx={sectionHeaderSx}>
              <LocationOnOutlinedIcon sx={{ color: AUTH_PRIMARY, fontSize: 22 }} />
              <Typography sx={{ ...authSectionLabelSx, mb: 0, mt: 0 }}>
                Where are you located?
              </Typography>
            </Box>

            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
              value={country}
              onChange={(_, newValue) => {
                setCountry(newValue);
                setState(null);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Country" required variant="outlined" placeholder="Select country" />
              )}
              sx={authFieldSx}
            />

            <Autocomplete
              options={states}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.isoCode === value.isoCode}
              value={state}
              onChange={(_, newValue) => setState(newValue)}
              disabled={!country}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Province / State"
                  required
                  variant="outlined"
                  placeholder={country ? 'Select province or state' : 'Select country first'}
                />
              )}
              sx={{ ...authFieldSx, mb: 0 }}
            />
          </Box>

          <Box sx={sectionCardSx}>
            <Box sx={sectionHeaderSx}>
              <PhoneOutlinedIcon sx={{ color: AUTH_PRIMARY, fontSize: 22 }} />
              <Typography sx={{ ...authSectionLabelSx, mb: 0, mt: 0 }}>
                How can we reach you?
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ color: '#565959', mb: 1, display: 'block' }}>
              We use this for appointment reminders and important updates.
            </Typography>

            <PhoneInput
              country={country?.isoCode.toLowerCase() ?? 'ca'}
              value={phone}
              onChange={setPhone}
              enableSearch
              searchPlaceholder="Search country…"
              {...phoneInputStyles}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !isFormValid}
            sx={{ ...authPrimaryButtonSx, mt: 0.5 }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Continue to CWY'}
          </Button>

          <Typography
            variant="caption"
            sx={{ mt: 2, color: '#9AA0A6', textAlign: 'center', display: 'block', lineHeight: 1.5 }}
          >
            Your information is kept private and only used to improve your CWY experience.
          </Typography>
        </Box>
      </AuthPageShell>

      <Dialog open={showPasswordPrompt} onClose={handleSkipPassword} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Set a password (optional)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: '#565959' }}>
            You signed in with Google. Add a password if you&apos;d also like to sign in with email.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={authFieldSx}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={authFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleSkipPassword} color="inherit">
            Skip for now
          </Button>
          <Button
            onClick={handleSavePassword}
            variant="contained"
            disabled={!password || !confirmPassword}
            sx={authPrimaryButtonSx}
          >
            Save password
          </Button>
        </DialogActions>
      </Dialog>
      <ToastHost />
    </>
  );
};
